import type { FeatureKey } from './subscription-plans'
import { ModuleTypeEnum } from './module-type'
import { FEATURE_KEYS } from './subscription-plans'

// Guided Setup step registry — the single source of truth for which onboarding
// steps exist and what subscription features gate them. Consumed by:
//  - apps/web guided flow (/getting-started) and the Today OnboardingWidget
//  - apps/api Benny MCP tools (onboarding_status)
//
// Gating rules (see CLAUDE.md "Personal vs Work Accounts"):
//  - `requiredFeatures` are subscription features that must ALL be enabled.
//    Personal accounts treat every subscription feature as enabled (the
//    subscription layer is inert for them), so business-only steps must ALSO
//    set `orgOnly: true` — a subscription gate alone is OPEN to personal.

export type OnboardingFlowPhaseId =
  | 'welcome'
  | 'data'
  | 'team'
  | 'tools'
  | 'benny'

export type OnboardingFlowStepId =
  | 'welcome'
  | 'create-project'
  | 'import-clients'
  | 'import-contacts'
  | 'import-tasks'
  | 'import-deals'
  | 'import-suppliers'
  | 'invite-team'
  | 'setup-rates'
  | 'connect-tools'
  | 'meet-benny'

export interface OnboardingFlowStepDef {
  id: OnboardingFlowStepId
  phase: OnboardingFlowPhaseId
  /** Subscription features that must ALL be enabled for the step to appear. */
  requiredFeatures: FeatureKey[]
  /** Hidden for PERSONAL accounts regardless of subscription gates. */
  orgOnly: boolean
  /** Set on import steps: the module the embedded ImportWizard targets. */
  importModule?: ModuleTypeEnum
}

export const ONBOARDING_FLOW_PHASES: Record<
  OnboardingFlowPhaseId,
  { title: string }
> = {
  welcome: { title: 'Welcome' },
  data: { title: 'Bring your data' },
  team: { title: 'Your team' },
  tools: { title: 'Your tools' },
  benny: { title: 'Meet Benny' },
}

/**
 * All flow steps in display order. Filter with `resolveOnboardingFlowSteps`.
 *
 * Feature grounding (SUBSCRIPTION_PLAN_CONFIGS):
 *  - Entities/contacts/projects/tasks exist on every plan (Free is limited by
 *    MAX_* caps, not access) → ungated.
 *  - DEALS, SUPPLIERS, TEAM (rates), INTEGRATIONS are false on Free, true on
 *    Pro and Business → gate those steps on the feature key, never a plan name.
 */
export const ONBOARDING_FLOW_STEPS: OnboardingFlowStepDef[] = [
  { id: 'welcome', phase: 'welcome', requiredFeatures: [], orgOnly: false },
  // Benny comes right after welcome so every plan meets its AI guide before
  // the data steps — he can then help with the imports that follow.
  { id: 'meet-benny', phase: 'benny', requiredFeatures: [], orgOnly: false },
  {
    id: 'create-project',
    phase: 'data',
    requiredFeatures: [],
    orgOnly: true,
  },
  {
    id: 'import-clients',
    phase: 'data',
    requiredFeatures: [],
    orgOnly: true,
    importModule: ModuleTypeEnum.ENTITY,
  },
  {
    id: 'import-contacts',
    phase: 'data',
    requiredFeatures: [],
    orgOnly: true,
    importModule: ModuleTypeEnum.CONTACT,
  },
  {
    id: 'import-tasks',
    phase: 'data',
    requiredFeatures: [],
    orgOnly: false,
    importModule: ModuleTypeEnum.TASK,
  },
  {
    id: 'import-deals',
    phase: 'data',
    requiredFeatures: [FEATURE_KEYS.DEALS],
    orgOnly: true,
    importModule: ModuleTypeEnum.DEAL,
  },
  {
    id: 'import-suppliers',
    phase: 'data',
    requiredFeatures: [FEATURE_KEYS.SUPPLIERS],
    orgOnly: true,
    importModule: ModuleTypeEnum.SUPPLIER,
  },
  { id: 'invite-team', phase: 'team', requiredFeatures: [], orgOnly: false },
  {
    id: 'setup-rates',
    phase: 'team',
    requiredFeatures: [FEATURE_KEYS.TEAM],
    orgOnly: true,
  },
  {
    id: 'connect-tools',
    phase: 'tools',
    requiredFeatures: [FEATURE_KEYS.INTEGRATIONS],
    orgOnly: false,
  },
]

/**
 * Subscription gates for the Today-widget checklist steps (the legacy
 * OnboardingStepId namespace). Steps not listed are ungated. Keeps the widget
 * honest on Free: no "set up rates" / "connect an integration" prompts for
 * features the plan doesn't include.
 */
export const WIDGET_STEP_FEATURE_GATES: Record<string, FeatureKey[]> = {
  'add-vendor': [FEATURE_KEYS.SUPPLIERS],
  'setup-rates': [FEATURE_KEYS.TEAM],
  'connect-integration': [FEATURE_KEYS.INTEGRATIONS],
}

export interface ResolveOnboardingFlowStepsOptions {
  isPersonal: boolean
  /** Subscription feature check; personal accounts should pass () => true. */
  checkFeature: (featureKey: FeatureKey) => boolean
}

export function resolveOnboardingFlowSteps({
  isPersonal,
  checkFeature,
}: ResolveOnboardingFlowStepsOptions): OnboardingFlowStepDef[] {
  return ONBOARDING_FLOW_STEPS.filter(step => {
    if (isPersonal && step.orgOnly) return false
    return step.requiredFeatures.every(key => checkFeature(key))
  })
}
